import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Tab, Tabs, Badge, Card, Form } from 'react-bootstrap';
import Swal from 'sweetalert2';
import axios from "axios";
import { FaUserAlt, FaSearch, FaTimes, FaHistory, FaFilter, FaUsers, FaCalendarAlt, FaPaperPlane, FaMobileAlt, FaApple, FaAndroid, FaChevronDown, FaChevronRight, FaBell, FaPlus } from "react-icons/fa";
import webservice from "./../../../services/webservice";
import { FaTimeline } from 'react-icons/fa6';
import { IoIosSend, IoMdNotifications } from "react-icons/io";
import { FaSun } from "react-icons/fa";
import NotificationTracking from './NotificationTracking';
import "./Notification.css"

const Notification = () => {
    const navigate = useNavigate();

    const [activeTab, setActiveTab] = useState("compose");
    const [notifications, setNotifications] = useState([]);
    const [title, setTitle] = useState("");
    const [subTitle, setSubTitle] = useState("");
    const [bodymessage, setMessage] = useState("");
    const [image, setImage] = useState(null);
    const [screenDestination, setScreenDestination] = useState("");
    const [dropsOfMedhorizoneBodymessage, setDropsOfMedhorizoneBodymessage] = useState("");
    // Targeting Mode: 'all', 'filtered', 'specific'
    const [targetMode, setTargetMode] = useState('all');

    // Filters
    const [filters, setFilters] = useState({
        age: "",
        ageRange: "",
        gender: "",
        maritalStatus: "",
        city: "",
        country: "",
        userType: "",
        status: "A", // Default Active
        deviceType: {
            ios: false,
            android: false,
            both: false
        }
    });

    // Specific Users
    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [isSearching, setIsSearching] = useState(false);

    const [notificationSent, setNotificationSent] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [progress, setProgress] = useState(0);

    const [pagination, setPagination] = useState({
        totalCount: 0,
        totalPages: 0,
        currentPage: 1,
        perPage: 10
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    // Date Range State (Default: Start=Today, End=Last 7 Days)
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 7);

    const [dateRange, setDateRange] = useState({
        startDate: sevenDaysAgo.toISOString().split('T')[0],
        endDate: todayStr
    });
    // Expanded Row State
    const [expandedRow, setExpandedRow] = useState(null);

    const screenOptions = [
        { label: "Water Reminder", key: "waterReminder" },
        { label: "BP List View", key: "bpListview" },
        { label: "Blood Sugar List View", key: "bsListview" },
        { label: "Insurance List View", key: "insuranceListview" },
        { label: "Mental Health", key: "mentalHealth" },
        { label: "Medication", key: "medication" },
        { label: "Mood Tracker", key: "mood" },
        { label: "AI Response", key: "AIResponse" },
        { label: "Drops Of Medhorizone", key: "story" }
    ];

    // Debounce search term
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            setCurrentPage(newPage);
        }
    };

    const handlePerPageChange = (value) => {
        setItemsPerPage(parseInt(value));
        setCurrentPage(1); // Reset to first page
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const handleDeviceChange = (val) => {
        setFilters(prev => ({
            ...prev,
            deviceType: {
                both: val === 'both',
                ios: val === 'ios',
                android: val === 'android'
            }
        }));
    };

    const searchUsers = async (query) => {
        setSearchQuery(query);
        if (query.length < 2) {
            setSearchResults([]);
            return;
        }
        setIsSearching(true);
        try {
            const response = await fetch(`${webservice}api/search-users?query=${query}`);
            const result = await response.json();
            if (result.success) {
                setSearchResults(result.data);
            }
        } catch (error) {
            console.error("Error searching users:", error);
        } finally {
            setIsSearching(false);
        }
    };

    const resetForm = () => {
        setTitle("");
        setSubTitle("");
        setMessage("");
        setImage('');
        setDropsOfMedhorizoneBodymessage("");
        setScreenDestination("");
        setSelectedUsers([]);
        setFilters({
            age: "",
            ageRange: "",
            gender: "",
            maritalStatus: "",
            city: "",
            country: "",
            userType: "",
            status: "A",
            deviceType: { ios: false, android: false, both: false }
        });
    };

    const addUser = (user) => {
        if (!selectedUsers.find(u => u._id === user._id)) {
            setSelectedUsers([...selectedUsers, user]);
        }
        setSearchQuery("");
        setSearchResults([]);
    };

    const removeUser = (userId) => {
        setSelectedUsers(selectedUsers.filter(u => u._id !== userId));
    };

    const sendNotification = async (e) => {
        e.preventDefault();

        // Prepare device list
        const selectedDevices = filters.deviceType.both
            ? ['I', 'A']
            : [
                ...(filters.deviceType.ios ? ['I'] : []),
                ...(filters.deviceType.android ? ['A'] : [])
            ];

        const payload = {
            title,
            subTitle,
            bodymessage,
            dropsOfMedhorizoneBodymessage,
            imageUrl: image,
            screenDestination,
            notificationType: screenDestination === "story" ? "morning" : "compose",
            adminId: localStorage.getItem('id') || 'Unknown', // Audit
            deviceType: selectedDevices,
            targetMode
        };

        if (targetMode === 'specific') {
            if (selectedUsers.length === 0) {
                Swal.fire("Error", "Please select at least one user.", "error");
                return;
            }
            payload.specificUserIds = selectedUsers.map(u => u._id);
        } else if (targetMode === 'filtered') {
            payload.age = filters.age || filters.ageRange;
            payload.ageRange = filters.age || filters.ageRange;
            payload.gender = filters.gender;
            payload.maritalStatus = filters.maritalStatus;
            payload.city = filters.city;
            payload.country = filters.country;
            payload.userType = filters.userType;
            payload.status = filters.status;
        }

        setIsSending(true);
        setProgress(0);
        const progressInterval = setInterval(() => {
            setProgress((prev) => {
                const step = Math.floor(Math.random() * 5) + 2;
                return prev + step >= 95 ? 95 : prev + step;
            });
        }, 500);

        try {
            const response = await fetch(webservice + 'api/send-Notification', {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            const result = await response.json();

            clearInterval(progressInterval);
            setProgress(100);

            // Wait a brief moment to show 100% before swal
            await new Promise(resolve => setTimeout(resolve, 300));

            if (result.message && (result.message.includes("successfully") || result.count > 0)) {
                setNotificationSent(true);
                Swal.fire({
                    icon: "success",
                    title: "Sent!",
                    text: `Notification sent to ${result.count} recipients.`,
                    confirmButtonColor: '#6c5ce7'
                });
                // Reset form
                setTitle("");
                setSubTitle("");
                setMessage("");
                setImage('');
                setDropsOfMedhorizoneBodymessage("");
                setScreenDestination("");
                setSelectedUsers([]);
                setFilters({
                    ageRange: "",
                    gender: "",
                    maritalStatus: "",
                    city: "",
                    country: "",
                    userType: "",
                    status: "A",
                    deviceType: { ios: false, android: false, both: false }
                });
            } else {
                Swal.fire({
                    icon: "info",
                    title: "Notice",
                    text: result.message,
                    confirmButtonColor: '#6c5ce7'
                });
            }
        } catch (error) {
            console.error("Error sending notification:", error);
            Swal.fire({
                icon: "error",
                title: "Failed",
                text: "Something went wrong. Please try again!",
                confirmButtonColor: '#d63031'
            });
        } finally {
            clearInterval(progressInterval);
            setIsSending(false);
            setProgress(0);
        }
    };

    return (
        <>
            <div className='notification-wrapper'>
                <div className='col-lg-12 col-xl-12 col-xxl-12'>
                    <div className='fade-in'>
                        <div className='form-section-card' style={{ padding: '24px 24px', marginTop: '20px', marginBottom: '20px', marginLeft: "23.5px", marginRight: "24px" }}>
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                                <div style={{ display: "flex", gap: "10px" }}>
                                    <div
                                        onClick={() => navigate('/notification')}
                                        className="nav-tab-btn active"
                                    >
                                        Compose notification
                                    </div>
                                    <div
                                        onClick={() => navigate('/scheduled-notification')}
                                        className="nav-tab-btn"
                                    >
                                        Scheduled Notification
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className='header-actions mb-4'>
                    <div className="custom-tabs-container">
                        <button className={`tab-btn ${activeTab === "compose" ? "active" : ""}`}
                            onClick={() => setActiveTab("compose")}>
                            <FaPaperPlane /> Compose
                        </button>
                        <button className={`tab-btn ${activeTab === "tracking" ? "active" : ""}`}
                            onClick={() => setActiveTab("tracking")}>
                            <FaTimeline /> Tracking
                        </button>
                    </div>
                </div>

                <div className="content-area">
                    {activeTab === "compose" && (
                        <div className="fade-in">
                            <form className="notify--compose-form" onSubmit={sendNotification}>
                                {/* Section 1: Notification Content */}
                                <div className="form-section-card">
                                    <div className="section-header">
                                        <div className="icon-circle">
                                            <FaBell />
                                        </div>
                                        <div className="section-title-group">
                                            <h4>Notification Content</h4>
                                        </div>
                                    </div>

                                    <div className="row">
                                        <div className="col-md-12 mb-4">
                                            <label className="modern-label">Select Category</label>
                                            <select
                                                value={screenDestination}
                                                onChange={(e) => setScreenDestination(e.target.value)}
                                                className="modern-select"
                                                required
                                            >
                                                <option value="">-- Select Category --</option>
                                                {screenOptions.map((option) => (
                                                    <option key={option.key} value={option.key}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="col-md-12 mb-4">
                                            <label className="modern-label">Campaign Title</label>
                                            <input
                                                type="text"
                                                value={title}
                                                maxLength={screenDestination === 'story' ? undefined : 100}
                                                onChange={(e) => setTitle(e.target.value)}
                                                placeholder="Enter Title"
                                                className="modern-input"
                                                required
                                            />
                                        </div>

                                        {screenDestination === 'story' && (
                                            <div className="col-md-12 mb-4">
                                                <label className="modern-label">Campaign Sub Title</label>
                                                <input
                                                    type="text"
                                                    value={subTitle}
                                                    maxLength={150}
                                                    onChange={(e) => setSubTitle(e.target.value)}
                                                    placeholder="Enter Sub Title"
                                                    className="modern-input"
                                                    required
                                                />
                                            </div>
                                        )}

                                        {screenDestination !== 'story' && (
                                            <div className="col-md-12 mb-4">
                                                <label className="modern-label">Notification Body</label>
                                                <textarea
                                                    rows={2}
                                                    value={bodymessage}
                                                    maxLength={200}
                                                    onChange={(e) => setMessage(e.target.value)}
                                                    placeholder="Write your engaging message here..."
                                                    className="modern-textarea"
                                                    required
                                                ></textarea>
                                            </div>
                                        )}

                                        {screenDestination === 'story' && (
                                            <div className="col-md-12 mb-4">
                                                <label className="modern-label">Drops of Medhorizone Body</label>
                                                <textarea
                                                    rows={4}
                                                    value={dropsOfMedhorizoneBodymessage}
                                                    onChange={(e) => setDropsOfMedhorizoneBodymessage(e.target.value)}
                                                    placeholder="Write your engaging message here..."
                                                    className="modern-textarea"
                                                    required
                                                ></textarea>
                                            </div>
                                        )}

                                        <div className="col-md-12 mb-2">
                                            <label className="modern-label">Image Upload</label>
                                            <input type="file" accept="image/*" className="modern-input" onChange={(e) => setImage(e.target.files[0])} />
                                        </div>
                                    </div>
                                </div>

                                {/* Section 2: Audience Selection */}
                                <div className="form-section-card">
                                    <div className="section-header">
                                        <div className="icon-circle">
                                            <FaUsers />
                                        </div>
                                        <div className="section-title-group">
                                            <h4>Users Selection</h4>
                                            <p>Who should receive this notification?</p>
                                        </div>
                                    </div>

                                    <div className="radio-group">
                                        <label className="custom-radio">
                                            <input type="radio" name="targetMode" checked={targetMode === 'all'} onChange={() => setTargetMode('all')} />
                                            <span className="radio-circle"></span>
                                            All Users
                                        </label>
                                        <label className="custom-radio">
                                            <input type="radio" name="targetMode" checked={targetMode === 'filtered'} onChange={() => setTargetMode('filtered')} />
                                            <span className="radio-circle"></span>
                                            Filtered Group
                                        </label>
                                        {/* <label className="custom-radio">
                                            <input type="radio" name="targetMode" checked={targetMode === 'specific'} onChange={() => setTargetMode('specific')} />
                                            <span className="radio-circle"></span>
                                            Specific Users
                                        </label> */}
                                    </div>

                                    {targetMode === 'filtered' && (
                                        <div className="filters-container slide-down pt-3">
                                            <hr className="mb-4" />
                                            <div className="row">
                                                <div className="col-md-3 mb-3">
                                                    <label className="modern-label">Age</label>
                                                    <select
                                                        className="modern-select"
                                                        value={filters.age || filters.ageRange || ""}
                                                        onChange={(e) => {
                                                            handleFilterChange('age', e.target.value);
                                                            handleFilterChange('ageRange', e.target.value);
                                                        }}
                                                    >
                                                        <option value="">All</option>
                                                        <option value="0-18">0-18</option>
                                                        <option value="19-25">19-25</option>
                                                        <option value="26-35">26-35</option>
                                                        <option value="36-45">36-45</option>
                                                        <option value="46-60">46-60</option>
                                                        <option value="Above 60">Above 60</option>
                                                    </select>
                                                </div>
                                                <div className="col-md-3 mb-3">
                                                    <label className="modern-label">Gender</label>
                                                    <select className="modern-select" value={filters.gender} onChange={(e) => handleFilterChange('gender', e.target.value)}>
                                                        <option value="">Any Gender</option>
                                                        <option value="Male">Male</option>
                                                        <option value="Female">Female</option>
                                                        <option value="Other">Other</option>
                                                    </select>
                                                </div>
                                                <div className="col-md-3 mb-3">
                                                    <label className="modern-label">Marital Status</label>
                                                    <select className="modern-select" value={filters.maritalStatus} onChange={(e) => handleFilterChange('maritalStatus', e.target.value)}>
                                                        <option value="">Any Status</option>
                                                        <option value="Single">Single</option>
                                                        <option value="Married">Married</option>
                                                        <option value="Divorced">Divorced</option>
                                                        <option value="Widowed">Widowed</option>
                                                    </select>
                                                </div>
                                                <div className="col-md-3 mb-3">
                                                    <label className="modern-label">Device</label>
                                                    <select
                                                        className="modern-select"
                                                        value={filters.deviceType.ios ? "ios" : filters.deviceType.android ? "android" : "both"}
                                                        onChange={(e) => handleDeviceChange(e.target.value)}
                                                    >
                                                        <option value="both">All</option>
                                                        <option value="ios">iOS</option>
                                                        <option value="android">Android</option>
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {targetMode === 'specific' && (
                                        <div className="specific-panel slide-down pt-3">
                                            <hr className="mb-4" />
                                            <div className="search-container mb-3" style={{ position: 'relative' }}>
                                                <FaSearch className="search-icon" style={{ position: 'absolute', left: '15px', top: '50%', transform: 'translateY(-50%)', color: '#999', zIndex: 1 }} />
                                                <input
                                                    type="text"
                                                    className="modern-input"
                                                    style={{ paddingLeft: '45px' }}
                                                    placeholder="Search users by name, email, or mobile..."
                                                    value={searchQuery}
                                                    onChange={(e) => searchUsers(e.target.value)}
                                                />
                                                {isSearching && <div className="spinner-border spinner-border-sm" style={{ position: 'absolute', right: '15px', top: '50%', transform: 'translateY(-50%)', zIndex: 1 }}></div>}
                                            </div>

                                            {searchResults.length > 0 && (
                                                <div className="search-results mb-3" style={{ maxHeight: '250px', overflowY: 'auto', border: '1px solid #eee', borderRadius: '12px', background: '#fff', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}>
                                                    {searchResults.map(user => (
                                                        <div key={user._id} className="search-result-item d-flex align-items-center p-3 border-bottom" style={{ cursor: 'pointer', transition: 'background 0.2s' }} onClick={() => addUser(user)}>
                                                            <div className="user-avatar-placeholder d-flex align-items-center justify-content-center bg-light rounded-circle" style={{ width: '36px', height: '36px', marginRight: '12px', fontWeight: 600, color: '#6c5ce7' }}>{user.name.charAt(0)}</div>
                                                            <div className="user-info flex-grow-1">
                                                                <div className="user-name fw-bold" style={{ fontSize: '0.95rem' }}>{user.name}</div>
                                                                <div className="user-email text-muted" style={{ fontSize: '0.85rem' }}>{user.email || user.mobile}</div>
                                                            </div>
                                                            <span className="badge bg-primary rounded-pill">+ Add</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            <div className="selected-users-area">
                                                {selectedUsers.length === 0 ? (
                                                    <div className="text-center p-4 border rounded-3 border-dashed" style={{ borderStyle: 'dashed' }}>
                                                        <p className="empty-state text-muted mb-0">No users selected yet.</p>
                                                    </div>
                                                ) : (
                                                    <div className="chips-container d-flex flex-wrap gap-2">
                                                        {selectedUsers.map(user => (
                                                            <div key={user._id} className="user-chip border rounded-pill px-3 py-1 d-flex align-items-center gap-2 bg-light shadow-sm">
                                                                <span style={{ fontSize: '0.85rem', fontWeight: 500 }}>{user.name}</span>
                                                                <FaTimes className="close-icon" style={{ cursor: 'pointer', fontSize: '0.75rem' }} onClick={() => removeUser(user._id)} />
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="notify-form-footer">
                                    <button type="button" className="btn-save shadow-sm" style={{ background: "#FF4D4D" }} onClick={resetForm} disabled={isSending}>
                                        <FaTimes /> Cancel
                                    </button>
                                    <button type="submit" className="btn-save shadow-sm" disabled={isSending} style={{ position: 'relative', overflow: 'hidden' }}>
                                        {isSending && (
                                            <div style={{
                                                position: 'absolute', top: 0, left: 0, height: '100%', width: `${progress}%`,
                                                backgroundColor: 'rgba(255, 255, 255, 0.2)', transition: 'width 0.5s ease-in-out'
                                            }}></div>
                                        )}
                                        <span style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                            {isSending ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                    Sending... {progress}%
                                                </>
                                            ) : (
                                                <><IoIosSend /> Send Notification</>
                                            )}
                                        </span>
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}
                    {activeTab === "history" && (
                        <div className="auditlog-wrapper" style={{ margin: '-1.875rem -1.875rem', width: 'calc(100% + 3.75rem)' }}>
                            <div className="auditlog-card" style={{ background: '#fff', boxShadow: 'none', border: 'none', overflow: 'hidden', width: '100%' }}>
                                <div className="auditlog-header" style={{
                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px', padding: '20px 25px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', width: '100%', boxSizing: 'border-box'
                                }}>
                                    <div className="header-left" style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                        <h4 className="card-title" style={{ color: '#fff', margin: 0, fontSize: '1.5rem', fontWeight: 600 }}>Notification Logs</h4>
                                        <span className="total-count" style={{ background: 'rgba(255, 255, 255, 0.2)', padding: '5px 12px', borderRadius: '20px', color: '#fff', fontSize: '0.875rem' }}>
                                            {pagination.totalCount} entries
                                        </span>
                                    </div>
                                    <div className="header-right" style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                                        <select
                                            className="per-page-select"
                                            value={itemsPerPage}
                                            onChange={(e) => handlePerPageChange(e.target.value)}
                                            style={{ width: 'auto', background: 'rgba(255, 255, 255, 0.9)', border: 'none', padding: '8px', borderRadius: '6px' }}
                                        >
                                            <option value={10}>10</option>
                                            <option value={25}>25</option>
                                            <option value={50}>50</option>
                                            <option value={100}>100</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="auditlog-body" style={{ padding: 0, minHeight: '400px', width: '100%' }}>
                                    <div className="search-section" style={{ padding: '20px 25px', borderBottom: '1px solid #e9ecef', display: 'flex', gap: '20px', flexWrap: 'wrap', alignItems: 'flex-end', width: '100%', boxSizing: 'border-box' }}>
                                        <div style={{ flex: 1, minWidth: '250px' }}>
                                            <label style={{ display: 'block', marginBottom: '8px', color: '#333', fontWeight: 500 }}>Search</label>
                                            <input
                                                type="text"
                                                className="search-input"
                                                placeholder="Search by Title or Message..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                style={{ width: '100%', padding: '10px 16px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.95rem', outline: 'none', height: '42px' }}
                                            />
                                        </div>
                                        <div className="notification-page">
                                            <div className="local-date-filter"
                                                style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>

                                                <div style={{ flex: 1, minWidth: '200px' }}>
                                                    <label className="date-label">Start Date</label>
                                                    <input
                                                        type="date"
                                                        value={dateRange.startDate}
                                                        max={dateRange.endDate}
                                                        onChange={(e) => {
                                                            setDateRange(prev => ({ ...prev, startDate: e.target.value }));
                                                            setCurrentPage(1);
                                                        }}
                                                        className="date-input"
                                                    />
                                                </div>

                                                <div style={{ flex: 1, minWidth: '200px' }}>
                                                    <label className="date-label">End Date</label>
                                                    <input
                                                        type="date"
                                                        value={dateRange.endDate}
                                                        min={dateRange.startDate}
                                                        max={todayStr}
                                                        onChange={(e) => {
                                                            setDateRange(prev => ({ ...prev, endDate: e.target.value }));
                                                            setCurrentPage(1);
                                                        }}
                                                        className="date-input"
                                                    />
                                                </div>

                                            </div>
                                        </div>


                                    </div>

                                    {notifications.length > 0 ? (
                                        <div className="notification-table-container" style={{ background: '#fff', width: '100%', padding: '0 25px', boxSizing: 'border-box' }}>
                                            <table className="notification-log-table" style={{ width: '100%', margin: 0, borderCollapse: 'collapse', background: '#fff', tableLayout: 'fixed' }}>
                                                <thead>
                                                    <tr>
                                                        <th style={{ width: '50px', background: '#f8f9fa', borderBottom: '2px solid #e9ecef', padding: '15px' }}></th>
                                                        <th style={{ width: '20%', background: '#f8f9fa', borderBottom: '2px solid #e9ecef', padding: '15px', fontWeight: 600, color: '#495057', textAlign: 'left' }}>Date</th>
                                                        <th style={{ width: '50%', background: '#f8f9fa', borderBottom: '2px solid #e9ecef', padding: '15px', fontWeight: 600, color: '#495057', textAlign: 'left' }}>User ID</th>
                                                        <th style={{ width: '20%', background: '#f8f9fa', borderBottom: '2px solid #e9ecef', padding: '15px', fontWeight: 600, color: '#495057', textAlign: 'center' }}>Future Count</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {notifications.map((group, index) => (
                                                        <React.Fragment key={index}>
                                                            <tr
                                                                style={{ borderBottom: '1px solid #e9ecef', cursor: 'pointer', background: expandedRow === index ? '#f8f9fa' : 'transparent' }}
                                                                onClick={() => setExpandedRow(expandedRow === index ? null : index)}
                                                            >
                                                                <td style={{ padding: '15px', verticalAlign: 'middle', textAlign: 'center', color: '#6c5ce7' }}>
                                                                    {expandedRow === index ? <FaChevronDown /> : <FaChevronRight />}
                                                                </td>
                                                                <td style={{ padding: '15px', verticalAlign: 'middle' }}>
                                                                    <span style={{ fontWeight: 600, color: '#2d3436' }}>{group._id.date}</span>
                                                                </td>
                                                                <td style={{ padding: '15px', verticalAlign: 'middle' }}>
                                                                    <span className="user-badge" style={{ background: '#e3f2fd', color: '#1976d2', padding: '6px 14px', borderRadius: '8px', fontSize: '0.9rem', fontWeight: 600 }}>
                                                                        {group._id.userId}
                                                                    </span>
                                                                </td>
                                                                <td style={{ padding: '15px', verticalAlign: 'middle', textAlign: 'center' }}>
                                                                    <span style={{ color: '#00b894', fontWeight: 600, fontSize: '1rem', background: '#e8f5e9', padding: '4px 12px', borderRadius: '20px' }}>
                                                                        {group.featureCount}
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                            {expandedRow === index && (
                                                                <tr>
                                                                    <td colSpan="4" style={{ padding: '20px', background: '#fcfcfc', borderBottom: '1px solid #e9ecef' }}>
                                                                        <div style={{ marginLeft: '20px' }}>
                                                                            <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 10px' }}>
                                                                                <thead>
                                                                                    <tr>
                                                                                        <th style={{ color: '#636e72', fontSize: '0.85rem', fontWeight: 600, paddingBottom: '10px' }}>Time</th>
                                                                                        <th style={{ color: '#636e72', fontSize: '0.85rem', fontWeight: 600, paddingBottom: '10px' }}>Type</th>
                                                                                        <th style={{ color: '#636e72', fontSize: '0.85rem', fontWeight: 600, paddingBottom: '10px' }}>Title</th>
                                                                                        <th style={{ color: '#636e72', fontSize: '0.85rem', fontWeight: 600, paddingBottom: '10px' }}>Message</th>
                                                                                        <th style={{ color: '#636e72', fontSize: '0.85rem', fontWeight: 600, paddingBottom: '10px' }}>Status</th>
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody>
                                                                                    {group.logs.map((log, logIndex) => (
                                                                                        <tr key={logIndex} style={{ background: '#fff', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                                                                                            <td style={{ padding: '15px', border: '1px solid #f1f2f6', borderRight: 'none', borderRadius: '8px 0 0 8px', color: '#2d3436' }}>
                                                                                                {new Date(log.sentAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                                                            </td>
                                                                                            <td style={{ padding: '15px', border: '1px solid #f1f2f6', borderRight: 'none', borderLeft: 'none' }}>
                                                                                                <span style={{ background: 'linear-gradient(135deg, #667eea10 0%, #764ba210 100%)', color: '#667eea', padding: '4px 10px', borderRadius: '6px', fontSize: '0.85rem', fontWeight: 600, textTransform: 'capitalize' }}>
                                                                                                    {log.notificationType || 'N/A'}
                                                                                                </span>
                                                                                            </td>
                                                                                            <td style={{ padding: '15px', border: '1px solid #f1f2f6', borderRight: 'none', borderLeft: 'none' }}>
                                                                                                <span style={{ color: '#2d3436', fontWeight: 500 }}>
                                                                                                    {log.title}
                                                                                                </span>
                                                                                            </td>
                                                                                            <td style={{ padding: '15px', border: '1px solid #f1f2f6', borderRight: 'none', borderLeft: 'none', color: '#636e72' }}>
                                                                                                {log.message}
                                                                                            </td>
                                                                                            <td style={{ padding: '15px', border: '1px solid #f1f2f6', borderLeft: 'none', borderRadius: '0 8px 8px 0' }}>
                                                                                                <span style={{
                                                                                                    background: log.status === 'success' ? '#e8f5e9' : log.status === 'failed' ? '#ffebee' : '#fff3e0',
                                                                                                    color: log.status === 'success' ? '#388e3c' : log.status === 'failed' ? '#d32f2f' : '#f57c00',
                                                                                                    padding: '4px 10px', borderRadius: '6px', fontSize: '0.85rem', fontWeight: 600, textTransform: 'capitalize'
                                                                                                }}>
                                                                                                    {log.status || 'Pending'}
                                                                                                </span>
                                                                                            </td>
                                                                                        </tr>
                                                                                    ))}
                                                                                </tbody>
                                                                            </table>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            )}
                                                        </React.Fragment>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <div className="empty-history" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', color: '#666' }}>
                                            <FaHistory className="empty-icon" style={{ fontSize: '3rem', marginBottom: '15px', opacity: 0.3 }} />
                                            <p>No campaigns found.</p>
                                        </div>
                                    )}
                                </div>

                                {/* Pagination */}
                                {notifications.length > 0 && (
                                    <div className="auditlog-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px 25px', background: '#f8f9fa' }}>
                                        <div className="pagination-info" style={{ color: '#666', fontSize: '0.875rem' }}>
                                            Showing {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, pagination.totalCount)} of {pagination.totalCount}
                                        </div>
                                        <div className="pagination-controls" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <button
                                                onClick={() => handlePageChange(currentPage - 1)}
                                                disabled={currentPage === 1}
                                                style={{ width: '36px', height: '36px', border: '1px solid #dee2e6', background: '#fff', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', opacity: currentPage === 1 ? 0.5 : 1 }}
                                            >
                                                &lt;
                                            </button>
                                            <span className="page-info" style={{ color: '#666', fontSize: '0.875rem' }}>
                                                Page {currentPage} of {pagination.totalPages}
                                            </span>
                                            <button
                                                onClick={() => handlePageChange(currentPage + 1)}
                                                disabled={currentPage === pagination.totalPages}
                                                style={{ width: '36px', height: '36px', border: '1px solid #dee2e6', background: '#fff', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: currentPage === pagination.totalPages ? 'not-allowed' : 'pointer', opacity: currentPage === pagination.totalPages ? 0.5 : 1 }}
                                            >
                                                &gt;
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    {activeTab === "tracking" && (
                        <div className="tracking-section fade-in">
                            <NotificationTracking />
                        </div>
                    )}
                </div>
            </div>
        </>
    )
}

export default Notification